import { z } from "zod";
import { AnalyticsPersonSourceSchema } from "./analytics.js";
import { MarketingPersonSourceSchema } from "./webinar-analytics.js";
/**
 * Рассылки офферов: an owner-approved offer that an OpenClaw agent writes
 * into each cohort person's existing chat (docs/campaigns/DESIGN.md).
 */
/** The OpenClaw agent of a Vekha employee (`ai_employees.openclaw_agent_id`). */
export const CampaignAgentIdSchema = z.string().regex(/^[a-z][a-z0-9_-]{0,63}$/u);
/** The writer when a campaign names none: Arkadiy, while he is an employee. */
export const DEFAULT_CAMPAIGN_AGENT_ID = "arkadiy";
export const CampaignStateSchema = z.enum(["draft", "scheduled", "running", "paused", "completed", "cancelled"]);
export const CampaignRecipientStateSchema = z.enum([
    "pending", "generating", "ready", "sending", "sent", "skipped", "failed", "cancelled",
]);
export const CampaignExclusionSchema = z.enum([
    "no_dialog", "inactive", "recent_dialog", "never_wrote", "other_campaign", "cooldown", "opt_out", "no_consultation",
]);
/** The agent's reading of the person's reply to the offer. */
export const CampaignReplyClassSchema = z.enum(["interested", "question", "not_interested", "negative", "opt_out"]);
export const CAMPAIGN_REPLY_LABELS = {
    interested: "Заинтересовался", question: "Задал вопрос", not_interested: "Не интересно",
    negative: "Негатив", opt_out: "Просит не писать",
};
export const CampaignOutcomeSchema = z.enum(["purchased", "returned_to_work"]);
/** What Vekha writes to amoCRM around a campaign message. */
export const CampaignCrmSettingsSchema = z.object({
    /** A note on the deal with the sent texts, so the manager sees the context. */
    noteAfterSend: z.boolean().default(true),
    /** A task for the deal's responsible when the person answers with interest or a question. */
    taskOnReply: z.boolean().default(true),
}).strict();
const ExternalId = z.string().regex(/^[1-9][0-9]{0,19}$/u);
const IsoDate = z.string().regex(/^20[0-9]{2}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12][0-9]|3[01])$/u);
const Clock = z.string().regex(/^(?:[01][0-9]|2[0-3]):[0-5][0-9]$|^24:00$/u);
export const CampaignCohortFilterSchema = z.object({
    /** amoCRM pipeline; «Отдел Продаж» by default. */
    pipelineExternalId: ExternalId.default("3394819"),
    stageExternalIds: z.array(ExternalId).min(1).max(20),
    /** Empty: every responsible. */
    responsibleExternalIds: z.array(ExternalId).max(30).default([]),
    /** Never these responsibles, e.g. Yana's own before-call responsible: "everyone after a call". */
    excludeResponsibleExternalIds: z.array(ExternalId).max(30).default([]),
    /** Only people whose last message in the chat is not older than this. */
    activeWithinDays: z.number().int().min(1).max(3650).nullable().default(null),
    /** Skip people who talked in the chat within this many days: a manager is still working with them. */
    quietForDays: z.number().int().min(1).max(90).nullable().default(null),
    /** Skip people another campaign wrote to within this many days. */
    cooldownDays: z.number().int().min(0).max(365).default(14),
    /** Deal closed (amoCRM closed_at) on or after this Moscow date, e.g. refusals of September. */
    closedFrom: IsoDate.nullable().default(null),
    /** Deal closed on or before this Moscow date (inclusive). */
    closedTo: IsoDate.nullable().default(null),
    /** Only people who had a held consultation, by the shared analytics' rule of «пришли» (core outreach.people, `held`):
     * an offer «после консультации» never goes to someone who had none (data 17.10.2026: three refusals of 29.09 did). */
    heldConsultation: z.boolean().default(false),
}).strict();
export const CampaignPaceSchema = z.object({
    hourlyLimit: z.number().int().min(1).max(120).default(30),
    /** Moscow time. */
    windowStart: Clock.default("10:00"),
    windowEnd: Clock.default("21:00"),
}).strict().refine(pace => clockMinutes(pace.windowStart) < clockMinutes(pace.windowEnd), {
    message: "Окно рассылки должно начинаться раньше, чем заканчиваться.",
    path: ["windowEnd"],
});
export const CampaignCohortPreviewInputSchema = z.object({
    filter: CampaignCohortFilterSchema,
    pace: CampaignPaceSchema.optional(),
    /** Editing this campaign: people it holds itself are not «already in another campaign». */
    campaignId: z.string().uuid().optional(),
}).strict();
const Bucket = z.object({ label: z.string(), count: z.number().int().nonnegative() }).strict();
export const CampaignCohortPreviewSchema = z.object({
    cohortDescription: z.string(),
    candidates: z.number().int().nonnegative(),
    included: z.number().int().nonnegative(),
    excluded: z.record(CampaignExclusionSchema, z.number().int().nonnegative()),
    byResponsible: z.array(Bucket),
    byStage: z.array(Bucket),
    byChannel: z.array(Bucket),
    estimatedWorkingHours: z.number().nonnegative(),
    estimatedFinishAt: z.string().nullable(),
    sample: z.array(z.object({
        dealExternalId: z.string(), contactName: z.string().nullable(), responsibleName: z.string().nullable(),
        stageName: z.string(), channelLabel: z.string().nullable(), lastActivityAt: z.string().nullable(),
    }).strict()).max(10),
}).strict();
export const CampaignCreateInputSchema = z.object({
    title: z.string().trim().min(3).max(200),
    /** The brief the agent writes from: what is offered, conditions, deadline, link. */
    offer: z.string().trim().min(20).max(8000),
    /** The writer: an active employee from `CampaignCatalog.writers`. */
    employeeId: z.string().uuid().optional(),
    /**
     * The writer by its OpenClaw agent id (the owner agent's tool: "arkadiy", "yana"),
     * resolved to the employee. Neither given: the employee of `DEFAULT_CAMPAIGN_AGENT_ID`.
     */
    agentId: CampaignAgentIdSchema.optional(),
    filter: CampaignCohortFilterSchema,
    pace: CampaignPaceSchema.default({ hourlyLimit: 30, windowStart: "10:00", windowEnd: "21:00" }),
    crm: CampaignCrmSettingsSchema.default({ noteAfterSend: true, taskOnReply: true }),
    idempotencyKey: z.string().min(16).max(160),
}).strict();
/**
 * Changes to a campaign that has not started (draft or scheduled), like a
 * PATCH: only the named fields change. A new `filter` replaces the whole
 * cohort filter and collects the recipients again (their sample texts go);
 * a new offer or writer keeps the recipients and sends their prepared texts
 * back to the agent. Title, pace and amoCRM settings touch no recipient.
 */
export const CampaignEditInputSchema = z.object({
    expectedVersion: z.number().int().positive(),
    title: z.string().trim().min(3).max(200).optional(),
    offer: z.string().trim().min(20).max(8000).optional(),
    /** The new writer: an active employee from `CampaignCatalog.writers`. */
    employeeId: z.string().uuid().optional(),
    /** The new writer by its OpenClaw agent id (owner agent's tool). */
    agentId: CampaignAgentIdSchema.optional(),
    filter: CampaignCohortFilterSchema.optional(),
    /** Only the named pace fields change; the window must still start before it ends. */
    pace: z.object({
        hourlyLimit: z.number().int().min(1).max(120).optional(),
        windowStart: Clock.optional(),
        windowEnd: Clock.optional(),
    }).strict().optional(),
    crm: z.object({ noteAfterSend: z.boolean().optional(), taskOnReply: z.boolean().optional() }).strict().optional(),
}).strict().refine(value => Object.keys(value).some(key => key !== "expectedVersion"), {
    message: "Укажите, что изменить.",
});
export const CampaignEditChangeSchema = z.enum(["title", "offer", "writer", "filter", "pace", "crm"]);
export const CampaignDeleteMutationSchema = z.object({
    expectedVersion: z.number().int().positive(),
}).strict();
export const CampaignDeleteResultSchema = z.object({
    id: z.string().uuid(),
    deleted: z.literal(true),
    /** Recipients removed with it; none of them had been written to. */
    recipients: z.number().int().nonnegative(),
}).strict();
/** Same cohort: arrays compare as sets, so the order of picked stages does not matter. */
export function sameCohortFilter(left, right) {
    const normal = (filter) => JSON.stringify({
        ...filter,
        stageExternalIds: [...filter.stageExternalIds].sort(),
        responsibleExternalIds: [...filter.responsibleExternalIds].sort(),
        excludeResponsibleExternalIds: [...filter.excludeResponsibleExternalIds].sort(),
    }, Object.keys(filter).sort());
    return normal(CampaignCohortFilterSchema.parse(left)) === normal(CampaignCohortFilterSchema.parse(right));
}
export const CampaignActionSchema = z.enum(["start", "schedule", "unschedule", "pause", "resume", "cancel"]);
export const CampaignStateMutationSchema = z.object({
    action: CampaignActionSchema,
    expectedVersion: z.number().int().positive(),
    /** Required for `schedule`: the moment the campaign starts by itself (ISO 8601 with offset). */
    scheduledStartAt: z.string().datetime({ offset: true }).optional(),
}).strict().refine(value => value.action !== "schedule" || value.scheduledStartAt !== undefined, {
    message: "Для планирования нужно время старта.",
    path: ["scheduledStartAt"],
});
export const CampaignSamplesMutationSchema = z.object({
    count: z.number().int().min(1).max(5),
    expectedVersion: z.number().int().positive(),
}).strict();
export const CampaignCountsSchema = z.object({
    total: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    ready: z.number().int().nonnegative(),
    sending: z.number().int().nonnegative(),
    sent: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    cancelled: z.number().int().nonnegative(),
    replied: z.number().int().nonnegative(),
    /** Delivered (not refused by the channel), no reply yet. */
    noReply: z.number().int().nonnegative(),
    interested: z.number().int().nonnegative(),
    question: z.number().int().nonnegative(),
    notInterested: z.number().int().nonnegative(),
    optOut: z.number().int().nonnegative(),
    /** The deal left the cohort stage for work after the message. */
    returnedToWork: z.number().int().nonnegative(),
    /** The person's first new-sale payment after the message (core `touch.purchases`); a purchase after several campaigns is
     * the last delivered one's (Ден 17.10.2026, «4 — Б»), so the buyers of all campaigns add up to the purchases. */
    purchased: z.number().int().nonnegative(),
    /** «Дошло» (Ден 17.10.2026, «3 — Б»: «получили» — by delivery): sent and not refused by the channel. Telegram reports no
     * «delivered» (only sent and read), so «not refused» is the delivery the data has. */
    delivered: z.number().int().nonnegative().default(0),
    /** «Не дошло»: sent, and the channel refused every message whose delivery it reported (SPAM, deleted account…). */
    undelivered: z.number().int().nonnegative().default(0),
    /** «Прочитали» (Ден 17.10.2026, «7 — Да, конечно»): delivered, and the channel marked a message of the campaign read or the
     * person answered — an answer is a reading too. WhatsApp hides the mark from people who turned read receipts off, so the
     * number can only be lower than the truth. */
    read: z.number().int().nonnegative().default(0),
}).strict();
export const CampaignSummarySchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
    /** The writing employee. */
    employeeId: z.string().uuid(),
    /** Its OpenClaw agent, fixed at creation. */
    agentId: CampaignAgentIdSchema,
    agentName: z.string(),
    state: CampaignStateSchema,
    version: z.number().int().positive(),
    counts: CampaignCountsSchema,
    pace: z.object({ hourlyLimit: z.number().int(), windowStart: z.string(), windowEnd: z.string() }).strict(),
    crm: z.object({ noteAfterSend: z.boolean(), taskOnReply: z.boolean() }).strict(),
    cohortDescription: z.string(),
    createdAt: z.string(),
    /** Planned start of a scheduled campaign (kept after it starts). */
    scheduledStartAt: z.string().nullable(),
    startedAt: z.string().nullable(),
    completedAt: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    nextSendAt: z.string().nullable(),
    estimatedFinishAt: z.string().nullable(),
}).strict();
export const CampaignEditResultSchema = z.object({
    campaign: CampaignSummarySchema,
    /** What actually changed; empty when the values were already the same. */
    changed: z.array(CampaignEditChangeSchema),
    /** The recipients were collected again from the new cohort filter. */
    cohortRebuilt: z.boolean(),
    /** Prepared texts sent back to the agent for the new offer or writer. */
    textsReset: z.number().int().nonnegative(),
}).strict();
export const CampaignListSchema = z.object({ campaigns: z.array(CampaignSummarySchema) }).strict();
export const CampaignMessageSchema = z.object({
    sequenceNumber: z.number().int().min(1).max(3),
    text: z.string(),
    state: z.string(),
    confirmedAt: z.string().nullable(),
    deliveryStatus: z.string().nullable(),
    /** The channel's code when it refused the message (`deliveryStatus` = error), e.g. SPAM. */
    deliveryError: z.string().nullable().optional(),
}).strict();
export const CampaignRecipientSchema = z.object({
    id: z.string().uuid(),
    position: z.number().int().positive(),
    dealExternalId: z.string(),
    dealUrl: z.string().nullable(),
    dealName: z.string().nullable(),
    contactName: z.string().nullable(),
    responsibleName: z.string().nullable(),
    stageName: z.string(),
    channelLabel: z.string().nullable(),
    conversationId: z.string().uuid().nullable(),
    state: CampaignRecipientStateSchema,
    isSample: z.boolean(),
    skipReason: z.string().nullable(),
    failureCode: z.string().nullable(),
    failureMessage: z.string().nullable(),
    generationNote: z.string().nullable(),
    /** Prepared texts before delivery; the sent texts are in `messages`. */
    draftParts: z.array(z.string()),
    messages: z.array(CampaignMessageSchema),
    sentAt: z.string().nullable(),
    repliedAt: z.string().nullable(),
    replyClass: CampaignReplyClassSchema.nullable(),
    replySummary: z.string().nullable(),
    outcome: CampaignOutcomeSchema.nullable(),
    /** The core's person of the recipient (`outreach.people`: the card of the delivery, else of the deal; before it — «Купили» of
     * `touch.purchases`): opens their full card. */
    personId: z.string().regex(/^P[0-9]{1,12}$/u).nullable().optional(),
    /** Where the person came from on the day of the send (core `outreach.people`, the rule of «Реклама»); null — no card or no
     * ads data for them («Нет карточки»); absent — the analytics has not answered for this recipient yet. */
    source: MarketingPersonSourceSchema.nullable().optional(),
}).strict();
export const CampaignRecipientFilterSchema = z.enum([
    "all", "sent", "replied", "interested", "purchased", "no_reply", "waiting", "skipped", "failed", "delivered", "undelivered", "read",
]);
/** A source of «Откуда» (person_source group: `<cabinet>:<campaign>`, `not_ad:<reason>`) or `unknown` — no card. */
export const CampaignSourceGroupSchema = z.string().regex(/^(unknown|[A-Za-z0-9_]{1,40}:[0-9A-Za-z_-]{1,60})$/u);
export const CampaignDetailQuerySchema = z.object({
    filter: CampaignRecipientFilterSchema.default("all"),
    /** Only the recipients of this source (a bar of «Откуда»). */
    from: CampaignSourceGroupSchema.optional(),
    /** A sheet's search (17.10, find): a part of the name or the deal's title, a deal's number, the phone (seven digits or more) or the
     * Telegram nick of the deal's contact. The list and its total narrow, the bars of «Откуда» stay the sheet's. */
    find: z.string().trim().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
    offset: z.coerce.number().int().min(0).max(100_000).default(0),
}).strict();
export const CampaignDetailSchema = z.object({
    campaign: CampaignSummarySchema.extend({
        offer: z.string(),
        filter: CampaignCohortFilterSchema,
    }),
    recipients: z.array(CampaignRecipientSchema),
    recipientsTotal: z.number().int().nonnegative(),
    filter: CampaignRecipientFilterSchema,
    /** «Откуда» of all recipients of the filter (every page), folded as core person_source.breakdown; absent without the analytics. */
    sources: z.array(AnalyticsPersonSourceSchema).max(20).optional(),
    /** «Откуда» of everyone who got the campaign (`state = sent`): the tile of the campaign page. */
    sentSources: z.array(AnalyticsPersonSourceSchema).max(20).optional(),
    /** Why it did not reach («Не дошло»): recipients by the channel's code, the most frequent first; null — no code. */
    undeliveredReasons: z.array(z.object({ code: z.string().nullable(), count: z.number().int().positive() }).strict()).max(20).optional(),
}).strict();
export const CampaignCatalogSchema = z.object({
    pipelines: z.array(z.object({
        externalId: z.string(),
        name: z.string(),
        stages: z.array(z.object({ externalId: z.string(), name: z.string(), deals: z.number().int().nonnegative() }).strict()),
    }).strict()),
    responsibles: z.array(z.object({
        externalId: z.string(), name: z.string(), deals: z.number().int().nonnegative(),
    }).strict()),
    /** Who can write a campaign: active employees with an OpenClaw agent. */
    writers: z.array(z.object({
        id: z.string().uuid(), name: z.string(), agentId: CampaignAgentIdSchema, role: z.string(),
    }).strict()),
    /** The writer used when none is chosen; null when there is no such employee. */
    defaultWriterId: z.string().uuid().nullable(),
}).strict();
export function clockMinutes(value) {
    const [hours, minutes] = value.split(":").map(Number);
    return (hours ?? 0) * 60 + (minutes ?? 0);
}
export function minutesClock(value) {
    const hours = Math.floor(value / 60), minutes = value % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}
const MOSCOW_OFFSET_MINUTES = 180;
/**
 * When `remaining` people are written at `hourlyLimit` inside the daily
 * Moscow window, starting at `from`. Moscow has no DST.
 */
export function estimateCampaignFinish(remaining, pace, from) {
    if (remaining <= 0)
        return null;
    const start = clockMinutes(pace.windowStart), end = clockMinutes(pace.windowEnd);
    let minutesNeeded = remaining * (60 / pace.hourlyLimit);
    let cursor = from.getTime();
    for (let day = 0; day < 400; day += 1) {
        const local = new Date(cursor + MOSCOW_OFFSET_MINUTES * 60_000);
        const minuteOfDay = local.getUTCHours() * 60 + local.getUTCMinutes();
        const dayStart = cursor - minuteOfDay * 60_000 - local.getUTCSeconds() * 1000 - local.getUTCMilliseconds();
        const windowFrom = Math.max(minuteOfDay, start);
        if (windowFrom < end) {
            const available = end - windowFrom;
            if (minutesNeeded <= available)
                return new Date(dayStart + (windowFrom + minutesNeeded) * 60_000);
            minutesNeeded -= available;
        }
        cursor = dayStart + 24 * 60 * 60_000;
    }
    return null;
}
export function workingHoursNeeded(remaining, hourlyLimit) {
    return Math.round((remaining / hourlyLimit) * 10) / 10;
}
//# sourceMappingURL=campaigns.js.map